import { useEffect, useMemo, useState } from 'react';
import { useAlertDialog } from '../../shared/AlertDialog';
import {
	listModerationItems,
	moderateItemAction,
	type ModerationItem,
	type ModerationStatusFilter,
	type ModerationTargetType
} from '@services/moderation';
import { getImageUrl } from '@utils/imageUrl';

export default function AdminModeration() {
	const { showAlert } = useAlertDialog();
	const [targetType, setTargetType] = useState<ModerationTargetType>('plan');
	const [statusFilter, setStatusFilter] = useState<ModerationStatusFilter>('active');
	const [searchQuery, setSearchQuery] = useState('');
	const [items, setItems] = useState<ModerationItem[]>([]);
	const [loading, setLoading] = useState(true);
	const [updatingId, setUpdatingId] = useState<number | null>(null);
	const [offset, setOffset] = useState(0);
	const [total, setTotal] = useState(0);
	const [expandedItemId, setExpandedItemId] = useState<number | null>(null);
	const limit = 15;

	function getAdminUserId(): number {
		try {
			const raw = window.sessionStorage.getItem('gardenhub:auth');
			if (!raw) return 0;
			const parsed = JSON.parse(raw) as { id?: string };
			return parsed?.id ? Number(parsed.id) : 0;
		} catch {
			return 0;
		}
	}

	useEffect(() => {
		setLoading(true);
		listModerationItems({
			target_type: targetType,
			status: statusFilter,
			query: searchQuery.trim() || undefined,
			limit,
			offset
		})
			.then((payload) => {
				setItems(payload.items ?? []);
				setTotal(payload.pagination?.total ?? 0);
			})
			.catch((err) => {
				console.error('Failed loading moderation items:', err);
				showAlert(err instanceof Error ? err.message : 'Failed to load moderation items.', { title: 'Error' });
				setItems([]);
				setTotal(0);
			})
			.finally(() => setLoading(false));
	}, [targetType, statusFilter, searchQuery, offset, showAlert]);

	const reportedCount = useMemo(
		() => items.filter((item) => (item.openReportCount ?? 0) > 0).length,
		[items]
	);

	async function handleAction(item: ModerationItem, action: 'soft_delete' | 'restore' | 'hide' | 'unhide') {
		const adminUserId = getAdminUserId();
		if (!adminUserId) {
			showAlert('Could not detect current admin session.', { title: 'Action blocked' });
			return;
		}
		const actionLabel =
			action === 'soft_delete'
				? 'soft delete'
				: action === 'restore'
					? 'restore'
					: action === 'hide'
						? 'hide'
						: 'unhide';
		if (!window.confirm(`Are you sure you want to ${actionLabel} this ${item.targetType}?`)) return;

		setUpdatingId(item.id);
		try {
			await moderateItemAction(item.targetType, item.id, {
				action,
				admin_user_id: adminUserId
			});
			const refreshed = await listModerationItems({
				target_type: targetType,
				status: statusFilter,
				query: searchQuery.trim() || undefined,
				limit,
				offset
			});
			setItems(refreshed.items ?? []);
			setTotal(refreshed.pagination?.total ?? 0);
		} catch (err) {
			console.error('Failed moderation action:', err);
			showAlert(err instanceof Error ? err.message : 'Moderation action failed.', { title: 'Error' });
		} finally {
			setUpdatingId(null);
		}
	}

	function getRowActions(item: ModerationItem): Array<{ id: string; label: string; action: 'soft_delete' | 'restore' | 'hide' | 'unhide' }> {
		const actions: Array<{ id: string; label: string; action: 'soft_delete' | 'restore' | 'hide' | 'unhide' }> = [];
		if (item.targetType === 'plan') {
			if (item.moderationStatus === 'visible') {
				actions.push({ id: 'hide', label: 'Hide', action: 'hide' });
				actions.push({ id: 'soft_delete', label: 'Delete', action: 'soft_delete' });
			} else if (item.moderationStatus === 'hidden') {
				actions.push({ id: 'unhide', label: 'Unhide', action: 'unhide' });
				actions.push({ id: 'soft_delete', label: 'Delete', action: 'soft_delete' });
			} else {
				actions.push({ id: 'restore', label: 'Restore', action: 'restore' });
			}
		} else if (item.moderationStatus === 'deleted') {
			actions.push({ id: 'restore', label: 'Restore', action: 'restore' });
		} else {
			actions.push({ id: 'soft_delete', label: 'Delete', action: 'soft_delete' });
		}
		return actions;
	}

	const typeTabs: Array<{ id: ModerationTargetType; label: string }> = [
		{ id: 'plan', label: 'Plans' },
		{ id: 'offer', label: 'Offers' },
		{ id: 'project', label: 'Projects' },
		{ id: 'user', label: 'Users' }
	];

	return (
		<>
			<h1 className="h3 mb-4">Moderation</h1>
			<div className="mb-3 d-flex flex-wrap gap-2">
				{typeTabs.map((tab) => (
					<button
						key={tab.id}
						type="button"
						className={`btn btn-sm ${targetType === tab.id ? 'btn-success' : 'btn-outline-secondary'}`}
						onClick={() => {
							setOffset(0);
							setTargetType(tab.id);
						}}
					>
						{tab.label}
					</button>
				))}
			</div>
			<div className="mb-3 d-flex flex-wrap gap-2">
				{(['active', 'hidden', 'deleted', 'all'] as const).map((s) => (
					<button
						key={s}
						type="button"
						className={`btn btn-sm ${statusFilter === s ? 'btn-primary' : 'btn-outline-secondary'}`}
						onClick={() => {
							setOffset(0);
							setStatusFilter(s);
						}}
					>
						{s === 'all' ? 'All' : s.charAt(0).toUpperCase() + s.slice(1)}
					</button>
				))}
			</div>
			<div className="mb-3 d-flex align-items-center gap-2">
				<input
					type="search"
					className="form-control form-control-sm"
					style={{ maxWidth: 360 }}
					placeholder={`Search ${targetType}s...`}
					value={searchQuery}
					onChange={(e) => {
						setOffset(0);
						setSearchQuery(e.target.value);
					}}
				/>
				{searchQuery && (
					<button
						type="button"
						className="btn btn-sm btn-outline-secondary"
						onClick={() => {
							setOffset(0);
							setSearchQuery('');
						}}
					>
						Clear
					</button>
				)}
				<span className="ms-auto small text-muted">Reported in page: {reportedCount}</span>
				<span className="small text-muted">Total: {total}</span>
			</div>

			<div className="card">
				<div className="card-body p-0">
					{loading ? (
						<div className="p-4 text-muted">Loading...</div>
					) : items.length === 0 ? (
						<div className="p-4 text-muted">No items match the current filter.</div>
					) : (
						<ul className="list-group list-group-flush">
							{items.map((item) => (
								<li key={`${item.targetType}-${item.id}`} className="list-group-item d-flex flex-wrap justify-content-between align-items-center gap-3">
									<div className="min-w-0">
										<div className="d-flex flex-wrap align-items-center gap-2">
											<strong>{item.title}</strong>
											{(item.openReportCount ?? 0) > 0 && (
												<span className="badge text-bg-danger">Reported {item.openReportCount}x</span>
											)}
											<span className={`badge ${item.moderationStatus === 'deleted' ? 'bg-danger' : item.moderationStatus === 'hidden' ? 'bg-warning text-dark' : 'bg-success'}`}>
												{item.moderationStatus ?? 'unknown'}
											</span>
										</div>
										<div className="small text-muted text-truncate">
											{item.subtitle || '-'} {item.entityStatus ? `• ${item.entityStatus}` : ''} • {new Date(item.createdAt).toLocaleString()}
										</div>
									</div>
									<div className="d-flex flex-wrap gap-2">
										<button
											type="button"
											className="btn btn-sm btn-outline-primary"
											onClick={() => setExpandedItemId((prev) => (prev === item.id ? null : item.id))}
										>
											{expandedItemId === item.id ? 'Hide info' : 'View more info'}
										</button>
										{getRowActions(item).map((action) => (
											<button
												key={action.id}
												type="button"
												className={`btn btn-sm ${
													action.action === 'soft_delete'
														? 'btn-outline-danger'
														: action.action === 'restore'
															? 'btn-outline-success'
															: 'btn-outline-secondary'
												}`}
												disabled={updatingId === item.id}
												onClick={() => handleAction(item, action.action)}
											>
												{action.label}
											</button>
										))}
										{updatingId === item.id && <span className="spinner-border spinner-border-sm" aria-hidden />}
									</div>
									{expandedItemId === item.id && (
										<div className="w-100 mt-3 pt-3 border-top">
											<div className="row g-3 align-items-start">
												{item.imageUrl && (
													<div className="col-12 col-md-3">
														<img
															src={getImageUrl(item.imageUrl) ?? item.imageUrl}
															alt={`${item.targetType} thumbnail`}
															className="img-fluid rounded border"
															style={{ maxHeight: 140, objectFit: 'cover', width: '100%' }}
														/>
													</div>
												)}
												<div className={item.imageUrl ? 'col-12 col-md-9' : 'col-12'}>
													<div className="small text-muted">
														<div><strong>ID:</strong> {item.id}</div>
														{typeof item.ownerUserId === 'number' && <div><strong>Owner user:</strong> #{item.ownerUserId}</div>}
														{typeof item.planId === 'number' && <div><strong>Plan:</strong> #{item.planId}</div>}
														<div><strong>Created:</strong> {new Date(item.createdAt).toLocaleString()}</div>
														<div><strong>Status:</strong> {item.entityStatus || '-'}</div>
														<div><strong>Moderation:</strong> {item.moderationStatus || '-'}</div>
														<div><strong>Reports:</strong> {item.openReportCount ?? 0}</div>
													</div>
												</div>
											</div>
										</div>
									)}
								</li>
							))}
						</ul>
					)}
				</div>
			</div>
			<div className="mt-3 d-flex align-items-center justify-content-between">
				<button
					type="button"
					className="btn btn-sm btn-outline-secondary"
					disabled={loading || offset === 0}
					onClick={() => setOffset((prev) => Math.max(0, prev - limit))}
				>
					Previous
				</button>
				<span className="small text-muted">
					Showing {Math.min(offset + 1, total)}-{Math.min(offset + items.length, total)} of {total}
				</span>
				<button
					type="button"
					className="btn btn-sm btn-outline-secondary"
					disabled={loading || offset + limit >= total}
					onClick={() => setOffset((prev) => prev + limit)}
				>
					Next
				</button>
			</div>
		</>
	);
}
